import { Component, EventEmitter, Input, OnInit, Output, ViewChildren } from '@angular/core';
import { NeqAbstractViewComponent } from '@sharedcommon/components/validation/abstract-view.component';
import { ValidationService } from '@sharedcommon/services/validation.service';
import { UserService } from '@sharedcommon/services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@sharedcommon/services/toast.service';
import { RawmaterialOrSampleService } from '@usercommon/service/rawmaterial-or-sample.service';
import { NeqResultsTableComponent } from '../results-tables/results-tables.component';
import { NeqRawmaterialsValuesDialogComponent } from '../../rawmaterials/dialog/rawmaterials-values.dialog';
import { Constants } from '@sharedcommon/util/constants.util';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { NeqReportDialogComponent } from '@usercommon/components/report/report.dialog';
import { DataSampleBulletinServiceClient, EntityServiceClient, ExportMultiFormatRequestDto, RawMaterialDto } from '@sharedrest/data.restservice';
import { Rights } from '@sharedcommon/util/rights.util';
import { ExportService } from '@usercommon/service/export.service';
import { HeaderService } from '@sharedcommon/services/header.service';
import { ActivatedRoute } from '@angular/router';
import { MatrixDto } from '@sharedrest/equation.restservice';
import {MatDialog} from '@angular/material/dialog';
import { CalculationStatus } from '@sharedcommon/model/calculation-status.model';
import { CalculationStatusService } from '@usercommon/service/calculation-status.service';
import {
  RawmaterialsExportDialogComponent
} from '@usercommon/components/rawmaterials/rawmaterials-export-dialog/rawmaterials-export-dialog.component';
import { RawMaterialOrSampleNutrientValueTableData } from '@sharedcommon/model/raw-material-or-sample-table-value-data.model';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';
import { NeqRawmaterialOrSampleResultsSettingsPopin } from '@usercommon/components/rawmaterials-or-samples/rawmaterials-or-samples-results-settings-popin/rawmaterials-or-samples-results-settings-popin.component';
import { SampleService } from '@usercommon/service/sample.service';
import { SampleDocumentTypeEnum } from '@sharedcommon/model/sample/sample-document.enum';
import { finalize } from 'rxjs';
import { DmsAndSampleService } from '@usercommon/service/dms-and-sample.service';
import {RawmaterialOrSampleStatus} from '@sharedcommon/util/rawmaterial-or-sample-status.utils';

@Component({
  selector: 'neq-rawmaterials-or-samples-results',
  templateUrl: './rawmaterials-or-samples-results.component.html'
})
export class NeqRawmaterialsOrSamplesResultsComponent extends NeqAbstractViewComponent implements OnInit {

  /** Sample or raw material ID */
  @Input()
  productId: string;
  @Input()
  calculationId: string;
  @Input()
  isReadOnly = false;
  @Input()
  matrices: MatrixDto[];
  @Input()
  // to indicates if the the value tab has been touched
  valueHasBeenTouched: boolean;
  @Output()
  hasBeenSaved = new EventEmitter<any>();

  rawMaterialOrSample: RawMaterialDto|SampleDto;
  tablesToShow: number[];

  currentCalculationStatus: CalculationStatus ;

  @ViewChildren(NeqResultsTableComponent) resultsTablesComponents: NeqResultsTableComponent[];
  error: any;

  // this is used to use a float layout instead of flex for results tables
  isIE = Constants.IS_INTERNET_EXPLORER;

  hasRights: boolean;

  isSampleMode: boolean;

  bulletinId: string;

  loadingDownloadBulletin: boolean = false;
  loadingSendBulletinMail: boolean = false;
  loadingSaveTemp: boolean = false;

  readonly ERROR_INVALID = 'invalid';

  constructor(
    private readonly userService: UserService,
    private readonly entityService: EntityServiceClient,
    private readonly translate: TranslateService,
    private readonly sampleBulletinService: DataSampleBulletinServiceClient,
    validation: ValidationService,
    protected readonly dialog: MatDialog,
    private readonly toaster: ToastService,
    private readonly headerService: HeaderService,
    private readonly activatedRoute: ActivatedRoute, private readonly toastService: ToastService,
    private readonly rawMatOrSampleService: RawmaterialOrSampleService,
    private readonly exportService: ExportService,
    private readonly sampleService: SampleService,
    private readonly dmsAndSampleService: DmsAndSampleService
  ) {
    super(validation);
    this.validationService.init();
    this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus() ;
  }

  ngOnInit() {
    this.isSampleMode = !this.userService.isFeedOperatingMode();
    this.validationService.dirty = this.hasFormModifications();
    this.error = this.rawMatOrSampleService.getError();
    this.hasRights = false;
    const valNbTable = this.userService.getMetaValue(Constants.USER_META_NB_TABLE_RESULT);
    if (FunctionUtils.isDefined(valNbTable)) {
      this.tablesToShow = new Array(Number(valNbTable)).fill(1).map((_x, i) => i + 1); // so ugly
    } else {
      this.tablesToShow = [1,2,3];
    }

    if (this.userService.isFeedOperatingMode() && this.userService.hasRights([Rights.U_MP_CALCULATE])) {
      this.hasRights = true;
    }
    this.retrieveRawMatAndSampleAndUpdateMetaData();
  }

  /**
   * Retrieve Raw mat or sample and update all meta data linked to it
   */
  retrieveRawMatAndSampleAndUpdateMetaData(): void {
    this.rawMatOrSampleService.retrieveProductById(this.productId)
      .subscribe(rawMatOrSample => {
        this.rawMaterialOrSample = rawMatOrSample;
        if (!this.userService.isFeedOperatingMode()) {
          const sample: SampleDto = rawMatOrSample as SampleDto;
          // Test the the edition authorization is tested on some sample attributes
          this.hasRights = this.sampleService.canEdit(sample);
          this.searchAndUpdateBulletinId(sample);
        }
        this.rawMatOrSampleService.setProductCalculationStatus(rawMatOrSample.calculationStatus) ;
        /* Update the current because the calc status of RawMat might change the current one */
        this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus() ;
        this.headerService.setHeaderValues({
          strongLabel: this.rawMaterialOrSample.code,
          basicLabel: ' - ' + this.rawMaterialOrSample.label,
          showGoBack: true,
          redirectionRoute: this.activatedRoute.parent.parent.snapshot
        });
      });
  }

  hasFormModifications(): boolean {
    return (FunctionUtils.isDefined(this.calculationId) && !this.rawMatOrSampleService.isSaved()) || this.valueHasBeenTouched;
  }

  private searchAndUpdateBulletinId(sample: SampleDto): void {
    this.bulletinId = sample.documents.find(doc => doc.type === SampleDocumentTypeEnum.BULLETIN)?.documentId ?? null;
  }

  save(): void {
    const saveCallback = () => {
      this.loadingSaveTemp = true;
      this.rawMatOrSampleService.saveTempCalcAndUpdateAllMetaDatas(this.calculationId, this.rawMaterialOrSample)
        .pipe(finalize(() => this.loadingSaveTemp = false))
        .subscribe(() => {
            this.toaster.toast(this.translate.instant('msg.user.rawmaterial.result.save.valid'));
            this.calculationId = null;
            this.valueHasBeenTouched = false;
            this.hasBeenSaved.next(null);
            this.validationService.dirty = this.hasFormModifications();
            const values = this.rawMatOrSampleService.getValues();
            this.rawMatOrSampleService.clearValues();
            // Restore the values like this if the user get back to values tab it will get the saved values
            this.prepareResetValues(values);
            this.resultsTablesComponents.forEach(resultTable => resultTable.refresh());
            if (!this.userService.isFeedOperatingMode()) {
              // Retrieve the new sample to get the new version and new bulletin ID (if there are) of the sample
              this.retrieveRawMatAndSampleAndUpdateMetaData();
            }
          });
    };
    if (this.error && this.error.type === this.ERROR_INVALID) {
      this.showConfirmation(this.translate.instant('msg.user.rawmaterial.values.save.invalid'), saveCallback);
    } else if (!this.rawMatOrSampleService.getIsCoherent()) {
      this.showConfirmation(this.translate.instant('msg.user.rawmaterial.values.save.coherence'), saveCallback);
    } else {
      saveCallback();
    }
  }

  reportRm(): void {
    this.dialog.open(NeqReportDialogComponent, {
      data: {
        rawMaterialId: this.productId
      }
    });
  }

  /**
   * It will set the flag to reset the values when we wil get back to values tab
   * @param values the values to erase
   */
  prepareResetValues(values: RawMaterialOrSampleNutrientValueTableData[]): void {
    values.forEach(value => {
      /* Set it at true to be erase the data when we go back to
       * the results tab */
      value.eraseAttributes = true;
    });
  }

  settings() {
    const dialog = this.dialog.open(NeqRawmaterialOrSampleResultsSettingsPopin, {
      data: {
        rawMaterialId: this.productId
      }
    });

    dialog.afterClosed().subscribe(result => {
      if (result) {
        this.toaster.toast(this.translate.instant('msg.user.preferences.updated'));
        this.resultsTablesComponents.forEach(x => {
          x.settingsChange();
        });
      }
    });
  }

  canExport(): boolean {
    return this.exportService.canExport(this.rawMaterialOrSample)
    && this.rawMaterialOrSample?.status == RawmaterialOrSampleStatus.ENABLED;
  }

  exportEnabled(): boolean {
    return this.exportService.exportEnabled()
    && this.rawMaterialOrSample?.status == RawmaterialOrSampleStatus.ENABLED;
  }

  returnExportTooltip(): string {
    return this.exportService.returnExportTooltip() ;
  }

  exportRequest() {
    if (!this.canExport()) {
      return;
    }

    const confirmationMessage = this.translate.instant('label.user.export.confirmation.multiple.rm');

    const dialogRef = this.dialog.open(RawmaterialsExportDialogComponent, {
      data: {
        message: confirmationMessage,
        rawMaterials: this.rawMaterialOrSample,
        defaultFormat: this.userService.getEntityDto().rawmatExportFormat,
        listFormat: this.userService.getEntityDto().rawmatExportList
      }
    });

    dialogRef.afterClosed().subscribe((optionnalFormatChose) => {
      if (optionnalFormatChose) {
        const request: ExportMultiFormatRequestDto = {
          refreshToken: 'DEPRECATED',
          rawMaterialsCodes: [{entityCode: this.rawMaterialOrSample.entity.identifier, rmCodes: [{rmCode: this.rawMaterialOrSample.code}]}],
          exportFormatList: optionnalFormatChose
        };

        this.entityService.rawMaterialsexportnotificationExportMultiFormatNotification(this.userService.getEntity(), request)
          .subscribe(() => {
            this.toastService.toast(this.translate.instant('label.user.export.request-successful'));
          });
      }
    });
  }

  private showConfirmation(message: string, callback: Function): void {

    const dialog = this.dialog.open(NeqRawmaterialsValuesDialogComponent, {
      data: {
        message: message
      }
    });

    dialog.afterClosed().subscribe(result => {
      if (result) {
        callback();
      }
    });
  }

  /**
   * Check if the current calculation status if valid
   * @returns true if the current calculation is valid
   */
  isCurrentCalcStatusValid(): boolean {
    return CalculationStatusService.isCalcStatusValid(this.currentCalculationStatus) ;
  }

  /**
   * Check if the current calculation status if blocked after calc
   * @returns true if the current calculation is blocked after calc
   */
  isCurrentCalcStatusBlockedAfterCalc(): boolean {
    return CalculationStatusService.isCalcStatusBlockedAfterCalc(this.currentCalculationStatus) ;
  }

  /**
   * Check if the current calculation status if blocked before calc
   * @returns true if the current calculation is blocked before calc
   */
  isCurrentCalcStatusBlockedBeforeCalc(): boolean {
    return CalculationStatusService.isCalcStatusBlockedBeforeCalc(this.currentCalculationStatus) ;
  }

  returnGenerateRmPdfTooltip() {
    return (this.calculationId && this.rawMatOrSampleService.getTempForcedCalcStatus()) ?
                            'msg.common.icon.rawmaterial.report.save-before'
                            : 'msg.common.icon.rawmaterial.report' ;
  }

  /**
   * Check if we can generate a pdf
   */
  canGenerateRmPdf(): boolean {
    if (this.loadingSaveTemp || this.hasFormModifications()) {
      return false;
    }

    const isNotComputeOrNotForcedStatusFromCompute = !this.calculationId || !this.rawMatOrSampleService.getTempForcedCalcStatus() ;
    const enableGenPdrAccordingStatus = (CalculationStatusService.isCalcStatusValid(this.currentCalculationStatus) && isNotComputeOrNotForcedStatusFromCompute)
                                          || CalculationStatusService.isCalcStatusEmpty(this.currentCalculationStatus) ;
    return this.userService.isFeedOperatingMode() && this.rawMaterialOrSample?.calculationDate && enableGenPdrAccordingStatus ;
  }

  getCalcStatusErrorMessage(): string {
    if (!this.isCurrentCalcStatusBlockedAfterCalc()) {
      return '';
    }
    return this.translate.instant('msg.user.rawmaterial.results.blocked-calculated-nut-present') ;
  }

  downloadBulletin(): void {
    this.loadingDownloadBulletin = true;
    this.dmsAndSampleService.downloadDocumentAndCreateEvent(
      this.bulletinId, this.rawMaterialOrSample as SampleDto, this.rawMatOrSampleService.getReferenceOfSampleOrRawMat().label
    )
    .pipe(finalize(() => this.loadingDownloadBulletin = false))
    .subscribe();
  }

  canSendSampleBulletinByMail(): boolean {
    return CalculationStatusService.isCalcStatusValid(this.rawMaterialOrSample?.calculationStatus) && !!this.bulletinId;
  }

  sendSampleBulletinByMail(): void {
    this.loadingSendBulletinMail = true;
    this.sampleBulletinService.sendBulletinByMail(this.rawMaterialOrSample.id)
      .pipe(finalize(() => this.loadingSendBulletinMail = false))
      .subscribe(() => this.toastService.toast(this.translate.instant("msg.user.sample.bulletin-sent-by-e-mail")))
  }
}
